import React, { Component, createRef } from 'react';
import { Route, Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import BEMHelper from '@ynap/bem-helper';
import { Button } from '@ynap/button';
import track from 'react-tracking';
import createAnalyticsEvent from '../analytics';
import { trackStepSubmit } from './analytics';
import { scrollIntoView } from '@ynap/scroll-utils';
import { RetryErrorPage } from '@ynap/error-page';

const bem = new BEMHelper('Step');

@track()
export default class Step extends Component {
    static propTypes = {
        className: PropTypes.string,
        messages: PropTypes.object,
        onChange: PropTypes.func.isRequired,
        path: PropTypes.string.isRequired,
        locale: PropTypes.object.isRequired,
        completed: PropTypes.bool,
        disabled: PropTypes.bool,
        title: PropTypes.string,
        children: PropTypes.any,
        editable: PropTypes.bool,
        completedOnMatch: PropTypes.bool,
        exact: PropTypes.bool,
        small: PropTypes.bool,
        timeline: PropTypes.bool,
        error: PropTypes.bool,
        fetchCart: PropTypes.func,
        isFetching: PropTypes.bool,
        headerElement: PropTypes.element,
        horizontal: PropTypes.bool,
        invisible: PropTypes.bool,
        summaryButtonText: PropTypes.string,
        shouldScroll: PropTypes.bool.isRequired,
        isCheckoutLayout: PropTypes.bool,
    };

    static defaultProps = {
        editable: true,
        invisible: false,
        children: renderChildren,
        shouldScroll: false,
        scrollability: true,
        isCheckoutLayout: false,
    };

    constructor(props) {
        super(props);
        this.key = ''; // not using this.state - we don't want to trigger render.
        this.trackSubmit = this.trackSubmit.bind(this);
        this.trackPage = this.trackPage.bind(this);
        this.stepRef = createRef();
        this.state = {
            scrollability: true,
        };
    }

    componentDidMount() {
        const { onChange } = this.props;
        onChange(this.props);
    }

    componentDidUpdate(prevProps) {
        const { completed, onChange } = this.props;
        if (prevProps.completed !== completed) {
            onChange(this.props);
        }
    }

    @track(createAnalyticsEvent)
    trackPage() {}

    shouldFireAnalytics = (match, location, lineItems) => {
        // we check the location key to see if we've newly arrived on this step.
        if (match && lineItems && location && location.key !== this.key) {
            this.key = location.key;
            return true;
        }
        return false;
    };

    @track(trackStepSubmit)
    trackSubmit() {}

    render() {
        const {
            className,
            path,
            index,
            title,
            children,
            locale,
            disabled: forceDisabled,
            completed,
            editable,
            completedOnMatch,
            exact,
            small,
            pageAnalyticsFn,
            location,
            lineItems,
            isFetching,
            headerElement,
            horizontal,
            invisible,
            shouldScroll,
            timeline = false,
            isCheckoutLayout,
            messages,
        } = this.props;

        const stepNumber = index + 1;

        return (
            <Route path={path}>
                {(matchProps) => {
                    // because we cannot use componentDidMount to determine whether or not this particular
                    // step is the active (being edited) step, we need to check on each render to see if
                    // we should send an analytics page event.
                    const fireAnalytics = this.shouldFireAnalytics(matchProps.match, location, lineItems);
                    if (fireAnalytics) {
                        // allow specific Step to override our default tracking if pageAnalyticsFn supplied as prop
                        if (pageAnalyticsFn) pageAnalyticsFn();
                        else this.trackPage();
                    }

                    // the content by default depends on the renderChildren function below (see also defaultProps above)
                    const content = typeof children === 'function' ? children({ ...matchProps, ...this.props, trackSubmit: this.trackSubmit }) : children;
                    const disabled = forceDisabled || (!content && !small);
                    const isNotMatch = exact && matchProps.match ? !matchProps.match.isExact : !matchProps.match;
                    const ticked = completed && !disabled && (completedOnMatch ? true : isNotMatch);
                    const indexModifier = `${ticked ? '' : 'step' + stepNumber}${disabled ? (ticked ? 'disabled' : '-disabled') : ''}`;
                    const titleModifier = `${ticked ? 'ticked' : ''}${disabled ? 'disabled' : ''}`;

                    // because annoyingly at this point we don't know if the path has matched
                    if (shouldScroll && pathMatches(this.props) && this.state.scrollability && !isFetching && this.stepRef.current && headerElement?.current) {
                        scrollIntoView(this.stepRef, headerElement, 'smooth', true);

                        // turn off scrollability off when on a step, to prevent scrolling on form state change
                        this.setState({ scrollability: false });
                    }

                    // turn on scrollability back on when not on step
                    if (shouldScroll && pathMatches(this.props) === false && this.state.scrollability === false && !isFetching) {
                        this.setState({ scrollability: true });
                    }

                    return invisible ? null : (
                        <div
                            className={bem(null, { disabled, ticked, small, horizontal, notEditable: !editable, isCheckoutLayout, timeline }, className)}
                            ref={this.stepRef}
                        >
                            <div className={bem('header', { ticked, small, horizontal, isFetching, timeline, location: location, isCheckoutLayout })}>
                                {!isCheckoutLayout && (
                                    <div className={bem(ticked ? 'tick' : 'index', { [indexModifier]: true, small, horizontal, isCheckoutLayout, timeline })} />
                                )}
                                <h2 className={bem('title', { [titleModifier]: true, small, horizontal, isCheckoutLayout, timeline })}>{title}</h2>
                            </div>
                            {content}

                            {ticked && editable && !isCheckoutLayout && (
                                <Link className={bem('edit')} to={path.replace(':locale', locale.id)}>
                                    {messages.purchasePath.edit()}
                                </Link>
                            )}
                        </div>
                    );
                }}
            </Route>
        );
    }
}

export function renderChildren(props) {
    const { match, disabled, completed, loading, error, placeholder, fallback = renderFallback, form, summary, shouldDisplaySummary } = props;

    if (!disabled) {
        if (match) {
            if (loading) {
                return typeof placeholder === 'function' ? placeholder(props) : null;
            }
            if (error) {
                return typeof fallback === 'function' ? fallback(props) : null;
            }
            if (shouldDisplaySummary) {
                return typeof summary === 'function' ? summary(props) : null;
            }

            return typeof form === 'function' ? form(props) : null;
        }
        if (completed) {
            return typeof summary === 'function' ? summary(props) : null;
        }
    }
}

export function renderFallback(props) {
    const { fetchCart, messages } = props;
    const errorMessages = {
        retry: {
            ...messages.retry,
            title: messages.retry.titleGeneric || messages.errors.global,
        },
    };

    return <RetryErrorPage className={bem('error')} messages={errorMessages} maxRetries={0} action={fetchCart} />;
}

export function pathMatches({ path, location, locale }) {
    const stepPath = path.replace(':locale', locale.id);
    const locationPath = location.pathname;
    return stepPath === locationPath;
}
